import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, memo } from 'react';
import { revealSidebarProject, revealSidebarDocument } from '../lib/sidebarReveal';
import { isTypingTarget } from '../lib/typingTarget';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { inboxTasks } from '../lib/taskFilters';
import { NotificationsProvider, useNotifications } from '../contexts/NotificationsContext';
import { useUndo } from '../contexts/UndoContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { NotificationBell } from '../components/NotificationBell';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ProjectUnreadDot from '../components/unread/ProjectUnreadDot';
import ChatUnreadDot from '../components/unread/ChatUnreadDot';
import { usePresence } from '../lib/viewing';
import { unreadChats } from '../lib/sidebarChats';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import OnboardingModal from '../components/onboarding/OnboardingModal';
import TutorialCenter from '../components/tutorials/TutorialCenter';
import SpotlightIntro from '../components/tutorials/SpotlightIntro';
import { projectsApi, settingsApi, trashApi, documentsApi } from '../services/api';
import {
  useProjectsQuery,
  useTeamsQuery,
  useSettingsQuery,
  useTrashCountQuery,
  useDocumentsQuery,
  useMyBotsQuery,
  useChatCountsQuery,
  useBotChatQuery,
  useDocumentQuery,
} from '../hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import { projectKeys, documentKeys } from '../lib/queryKeys';
import { useTasksQuery } from '../hooks/queries';
import {
  Sparkles, CheckSquare, House, MessageSquare,
  Focus, Repeat, Globe,
  ChevronDown, ChevronRight, Plus, Settings, LogOut,
  Menu, X, Folder, Users, MoreHorizontal,
  PanelLeftClose, PanelLeftOpen, ChevronsRight, Search,
  Webhook, Mail, Trash2,
  FileText, Presentation, Pencil, Link2, Shield,
  ArrowDownAZ, ArrowUpAZ, Network, History, Database, Bot, ListChecks,
} from 'lucide-react';
import { useRole } from '../hooks/useRole';
import { useEntitlements } from '../hooks/useEntitlements';

// headerToolItems is defined inside AppLayout component (needs t() for translations)
import { useDateTime } from '../contexts/DateTimeContext';
import GlobalSearch from '../components/GlobalSearch';
import { chatIdFromUrl, newChatPath } from '../components/bots/chatLinks';
import { preloadEditor } from '../components/LazyBlockNoteEditor';
import BusyBeeLogo, { BusyBeeIcon } from '../components/BusyBeeLogo';
import UnifiedTaskCreation from '../components/task/UnifiedTaskCreation';
import UnifiedTaskDetail from '../components/task/UnifiedTaskDetail';
import { useTask } from '../contexts/TaskContext';
import KeyboardShortcutsModal from '../components/KeyboardShortcutsModal';
import AddProjectModal from '../components/AddProjectModal';
import AddProjectToTeamModal from '../components/AddProjectToTeamModal';
import ProjectContextMenu from '../components/ProjectContextMenu';
import ShareProjectModal from '../components/ShareProjectModal';
import ActivityLogModal from '../components/ActivityLogModal';
import CreateMenu from '../components/create/CreateMenu';
import NewPanel from '../components/create/NewPanel';
import SidebarProjectChats from '../components/layout/SidebarProjectChats';
import SidebarRecentChats from '../components/layout/SidebarRecentChats';
import SidebarFooter from '../components/layout/SidebarFooter';
import { ShellActionsContext } from '../components/layout/shellActions';
import { greetingText, todayText } from '../components/home/greeting';
import { AttentionPill, CountPill, IconSlot, ProjectDot, ProjectTile, SectionButton, SectionHeader, rowClass, teamSuffixClass } from '../components/layout/SidebarParts';
import { BotsProvider } from '../contexts/BotsContext';
import DeleteProjectModal from '../components/DeleteProjectModal';
import MobileBottomNav from '../components/mobile/MobileBottomNav';
import MobileHeader from '../components/mobile/MobileHeader';
import { MobileTitleContext, isChatScreen, isDocumentScreen, mobileScreen } from '../components/mobile/mobileTitle';
import MobileMoreSheet from '../components/mobile/MobileMoreSheet';
import PullToRefreshIndicator from '../components/mobile/PullToRefreshIndicator';
import RouteProgressBar from '../components/RouteProgressBar';
import { RouteChunkFallback } from '../components/skeletons/PageSkeletons';
import { useIsMobile } from '../hooks/useIsMobile';
import { useMediaQuery, SCREEN } from '../hooks/useMediaQuery';
import TabScreens from './TabScreens';
import { useVisualViewport } from '../hooks/useVisualViewport';
import { useModalBackClose } from '../hooks/useModalBackClose';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';
import { EMPTY_ARRAY } from '../lib/empty';

// The shell's dialogs, closed nearly all the time. The layout draws again on every
// navigation (it follows the address); these only when their own props change.
const TaskCreationDialog = memo(UnifiedTaskCreation);
const TaskDetailPanel = memo(UnifiedTaskDetail);
const SearchDialog = memo(GlobalSearch);
const AddProjectDialog = memo(AddProjectModal);
const AddProjectToTeamDialog = memo(AddProjectToTeamModal);
const ShareProjectDialog = memo(ShareProjectModal);
const ActivityLogDialog = memo(ActivityLogModal);
const OnboardingDialog = memo(OnboardingModal);
const Spotlight = memo(SpotlightIntro);
const MoreSheet = memo(MobileMoreSheet);
const NewDialog = memo(NewPanel);

// mainNavItems is defined inside AppLayout component (needs t() for translations)


function SortableProjectRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  // touchAction: 'pan-y' allows vertical scrolling to work natively on touch devices;
  // drag is started by the TouchSensor's long-press delay, not by any touch movement.
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, touchAction: 'pan-y' }}
      onDragStart={(e) => e.preventDefault()} // prevent native <a> drag interfering with dnd-kit
      {...attributes}
      {...listeners}
    >
      {/* Disable pointer events on children while dragging so the click that fires
          synchronously after pointerup (before React re-renders) doesn't navigate */}
      <div style={isDragging ? { pointerEvents: 'none' } : undefined}>
        {children}
      </div>
    </div>
  );
}

function UnreadBadge({ inline = false }) {
  const { unreadCount } = useNotifications();
  if (!unreadCount) return null;
  const label = unreadCount > 99 ? '99+' : unreadCount;
  if (inline) {
    return (
      <span className="ml-auto min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-[18px] text-center rounded-full bg-red-500 text-white">
        {label}
      </span>
    );
  }
  return (
    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 text-[10px] font-bold leading-4 text-center rounded-full bg-red-500 text-white pointer-events-none">
      {label}
    </span>
  );
}

// Secondary navigation items - in "More" section

// How many of the projects opened lately the sidebar lists under "Projects".
const RECENT_PROJECTS = 5;


const AppLayout = () => {
  const { t, i18n } = useTranslation();
  // This screen says whether the person uses it (lib/viewing.js): phones wait meanwhile.
  usePresence();
  const { isAdmin } = useRole();
  const entitlements = useEntitlements();
  const { has, plan, isFree, limitOf } = entitlements;

  // The four places, the same on desktop and phones (MobileBottomNav): Home (today), Tasks
  // (all my work; the Planner is its calendar; its badge: open tasks not yet on the calendar),
  // Chats (every conversation with every bot; managing bots is reached from there) and
  // Projects. The rest is under "More".
  const mainNavItems = useMemo(() => [
    { name: t('nav.home'), icon: House, path: '/app', match: (p) => p === '/app' || p === '/app/agenda' },
    { name: t('nav.tasks'), icon: CheckSquare, path: '/app/tasks', count: 'inbox', match: (p) => p === '/app/tasks' || p === '/app/planner' },
    { name: t('nav.chats'), icon: MessageSquare, path: '/app/chats', count: 'chats', testId: 'nav-chats',
      match: (p) => p === '/app/chats' || p.startsWith('/app/chats/') || p === '/app/bots' || p.startsWith('/app/bots/') },
    // An open project lights its own row; the place is lit on the list of all projects.
    { name: t('nav.projects'), icon: Folder, path: '/app/projects', match: (p, search) => p === '/app/projects' && !new URLSearchParams(search).get('project') },
  ], [t]);

  const headerToolItems = useMemo(() => [
    // The person's own short list of tasks from every project (pages/app/SpotlightPage.jsx).
    { name: t('spotlight.title'), icon: Sparkles, path: '/app/spotlight' },
    { name: t('nav.documents'), icon: FileText, path: '/app/documents' },
    ...(GOOGLE_APP_INTEGRATIONS ? [{ name: t('nav.email'), icon: Mail, path: '/app/gmail', gatedFeature: 'gmail' }] : []),
    { name: t('nav.teams'), icon: Users, path: '/app/teams', gatedFeature: 'teams' },
    { name: t('nav.habits'), icon: Repeat, path: '/app/habits' },
    { name: t('nav.focus_time'), icon: Focus, path: '/app/focus-time' },
    { name: t('nav.webhooks'), icon: Webhook, path: '/app/webhooks', gatedFeature: 'webhooks' },
    { name: t('nav.trash'), icon: Trash2, path: '/app/trash' },
    ...(isAdmin ? [{ name: t('nav.admin'), icon: Shield, path: '/app/admin' }] : []),
  ], [t, isAdmin]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const isMobile = useIsMobile();
  // The sidebar is drawn only where it shows (lg and up); phones and tablets open the
  // same content in the drawer below.
  const desktop = useMediaQuery(SCREEN.lg);
  const tablet = useMediaQuery(SCREEN.md) && !desktop;
  // Phones have the bottom tab bar: its screens stay drawn between taps (TabScreens).
  const phoneTabs = !useMediaQuery(SCREEN.md);
  useVisualViewport();
  // On mobile, route the Back button to closing the topmost open modal.
  useModalBackClose();
  const { openTaskCreation, openTaskDetail } = useTask();
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    projects: true
  });

  // "More" in the sidebar stays as the person left it.
  const [moreOpen, setMoreOpenState] = useState(() => {
    try { return localStorage.getItem('bb.sidebar.more') === '1'; } catch { return false; }
  });
  const setMoreOpen = (fn) => setMoreOpenState((prev) => {
    const next = typeof fn === 'function' ? fn(prev) : fn;
    try { localStorage.setItem('bb.sidebar.more', next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
  const shortcutLabel = mac ? '⌘/' : 'Ctrl /';
  const newShortcutLabel = mac ? '⌘K' : 'Ctrl K';
  // "New" (components/create/NewPanel.jsx): the one input and the create tiles, in a project
  // filled in with it. null while closed.
  const [newFor, setNewFor] = useState(null);  // { projectId, sectionId }

  // Sidebar collapse state: 0=expanded, 1=icons only, 2=fully hidden
  const [sidebarCollapse, setSidebarCollapse] = useState(0);

  // Data states
  const qc = useQueryClient();
  const projectsQuery = useProjectsQuery();
  const teamsQuery = useTeamsQuery();
  const trashCountQuery = useTrashCountQuery();
  const projects = projectsQuery.data || EMPTY_ARRAY;
  const trashCount = trashCountQuery.data?.count || 0;
  // Teams name a team project's team (it shows as a muted label, never as a second list).
  const teams = teamsQuery.data || EMPTY_ARRAY;

  // My Tasks' count (open tasks not yet on the calendar) comes from the shared tasks cache so it
  // refreshes automatically alongside every other tasks reader, with no
  // separate axios call.
  const { user, logout } = useAuth();
  const tasksQuery = useTasksQuery();
  const inboxCount = useMemo(() => inboxTasks(tasksQuery.data, user?.id).length, [tasksQuery.data, user]);
  // Bots is a page in the main nav: it shows how many approvals wait for the person.
  const { data: myBotsData } = useMyBotsQuery();
  const botsWaiting = (myBotsData?.bots || []).reduce((sum, b) => sum + (b.pending_approvals || 0), 0);
  const botsUnread = unreadChats(myBotsData);
  // Chats live under their project: how many each has (a project with only chats
  // still opens) and whether something waits there.
  const { data: chatCountsData } = useChatCountsQuery();
  const chatCounts = chatCountsData?.projects || {};
  const documentsQuery = useDocumentsQuery();
  const documents = documentsQuery.data || [];
  const settingsQuery = useSettingsQuery();
  // Show post-registration onboarding modal until the user finishes or skips it.
  // Existing accounts were migrated to onboarding_completed=true on backend startup,
  // so this only triggers for freshly-registered users.
  const showOnboarding =
    settingsQuery.data && settingsQuery.data.onboarding_completed === false;
  const [sections, setSections] = useState({});  // { projectId: [sections] }
  const [addMenu, setAddMenu] = useState({ show: false, x: 0, y: 0, projectId: null, parentId: null });
  const [itemMenu, setItemMenu] = useState({ show: false, x: 0, y: 0, type: null, item: null, projectId: null }); // type: 'section' | 'document'
  const [renamingItem, setRenamingItem] = useState(null); // { type, id }
  const [renameValue, setRenameValue] = useState('');
  const [expandedDocs, setExpandedDocs] = useState(() => new Set());  // folder doc IDs that are expanded in the sidebar
  const itemMenuRef = useRef(null);

  // Hover states
  const [projectsHovered, setProjectsHovered] = useState(false);
  const [favoritesHovered, setFavoritesHovered] = useState(false);
  const [hoveredProjectId, setHoveredProjectId] = useState(null);
  const [hoveredItemId, setHoveredItemId] = useState(null);

  // Drag & drop / sort state
  const [sectionSortOrders, setSectionSortOrders] = useState({});
  const [userProjectOrders, setUserProjectOrders] = useState({});
  // Desktop: start dragging after 6px of mouse movement.
  // Mobile: start dragging after a 250ms long-press (moving the finger before then
  // scrolls the sidebar normally). Tolerance allows slight finger movement during the
  // hold without cancelling the drag.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  // Modal states
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [addProjectForTeam, setAddProjectForTeam] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showAddToTeamModal, setShowAddToTeamModal] = useState(false);
  const [projectToAddToTeam, setProjectToAddToTeam] = useState(null);
  const [shareModalProject, setShareModalProject] = useState(null);
  const [activityLogProject, setActivityLogProject] = useState(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, project: null });

  // User menu state
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Pull-to-refresh — wires the mobile <main> scroll container to TanStack Query.
  // On pull, invalidate the queries that drive the visible UI so the page reloads
  // fresh data without needing a hard browser refresh.
  const mainScrollRef = useRef(null);
  const handleMobileRefresh = useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['tasks'] }),
      qc.invalidateQueries({ queryKey: ['projects'] }),
      qc.invalidateQueries({ queryKey: ['documents'] }),
      qc.invalidateQueries({ queryKey: ['notifications'] }),
      qc.invalidateQueries({ queryKey: ['habits'] }),
      qc.invalidateQueries({ queryKey: ['settings'] }),
      qc.invalidateQueries({ queryKey: ['trash'] }),
    ]);
  }, [qc]);
  const location = useLocation();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // The editor is not part of the start; it comes right after, before anyone opens a task.
  useEffect(() => {
    const id = setTimeout(preloadEditor, 1500);
    return () => clearTimeout(id);
  }, []);

  // Pages that fill the content panel edge to edge (a chat, like Grok Bot): on desktop next to
  // the sidebar; on phones full screen, with their own header and without the tab bar. Chats'
  // history is one too from tablets up (the history beside the chat); on phones it is a page.
  const fullBleed = isChatScreen(location.pathname);
  const wideBleed = fullBleed || location.pathname === '/app/chats';
  // A document keeps the panel's padding and the tab bar but is as tall as the panel.
  const docFit = isDocumentScreen(location.pathname);

  // The phone's top bar: the screen's name (or one a page sets with useMobileTitle)
  // and, below the bottom tabs, a back arrow. Back goes to the previous screen when
  // there is one in this session, else to the screen's parent.
  const [mobileTitleOverride, setMobileTitleOverride] = useState(null);
  const screen = mobileScreen(location.pathname, location.search);
  const mobileTitle = mobileTitleOverride || (screen.key ? t(screen.key) : '');
  const goBack = useCallback(() => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(screen.parent || '/app');
  }, [navigate, screen.parent]);

  const { pullDistance, refreshing } = usePullToRefresh({
    scrollRef: mainScrollRef,
    onRefresh: handleMobileRefresh,
    enabled: isMobile && !fullBleed && !docFit,
  });

  // App pages scroll inside <main> (mainScrollRef), not the window, so the
  // global window ScrollToTop can't reset them - navigating from a scrolled
  // page left the destination at the previous offset. Reset the container to
  // the top on pathname change. Keyed on pathname only (not search) so opening
  // a task via ?task= doesn't jump the underlying list to the top.
  // On phones TabScreens does it, giving each tab back its own position.
  useEffect(() => {
    if (phoneTabs) return;
    mainScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, phoneTabs]);

  const { timezoneMismatch, acceptTimezoneChange, dismissTimezoneMismatch } = useDateTime();
  const { triggerUndo, clearUndoStack, pushUndo } = useUndo();
  const { syncFromBackend } = useTheme();
  const { syncFromBackend: syncLanguage } = useLanguage();
  // Sync DB language only once per app-shell mount. After that the user's
  // in-app language switch (LanguageSwitcher → setLanguage) is the source of
  // truth; subsequent settings refetches must not race against it.
  const languageSyncedRef = useRef(false);

  // Imperative wrapper kept for legacy callers; the queries above keep the
  // sidebar reactive automatically via the global mutation cache invalidator.
  const fetchSidebarData = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['projects'] });
    qc.invalidateQueries({ queryKey: ['teams'] });
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['trash'] });
    qc.invalidateQueries({ queryKey: ['settings'] });
  }, [qc]);

  // Apply settings → local UI state once when settings resolve.
  useEffect(() => {
    const s = settingsQuery.data;
    if (!s) return;
    if (s.ui_preferences?.sidebar_collapsed !== undefined) {
      setSidebarCollapse(s.ui_preferences.sidebar_collapsed);
    }
    if (s.ui_preferences?.project_orders) {
      setUserProjectOrders(s.ui_preferences.project_orders);
    }
    if (s.ui_preferences?.theme) {
      syncFromBackend(s.ui_preferences.theme);
    }
    if (!languageSyncedRef.current && s.ui_preferences) {
      syncLanguage(s.ui_preferences.language);
    }
    languageSyncedRef.current = true;
  }, [settingsQuery.data, syncFromBackend, syncLanguage]);

  // Toggle sidebar collapse (two-stage: expanded -> icons -> hidden -> expanded)
  const toggleSidebarCollapse = useCallback(async () => {
    const newState = (sidebarCollapse + 1) % 3;
    setSidebarCollapse(newState);
    try {
      await settingsApi.update({ 'ui_preferences.sidebar_collapsed': newState });
    } catch (err) {
      console.error('Error saving sidebar state:', err);
    }
  }, [sidebarCollapse]);

  // Expand sidebar (from any collapsed state to expanded)
  const expandSidebar = useCallback(async () => {
    setSidebarCollapse(0);
    try {
      await settingsApi.update({ 'ui_preferences.sidebar_collapsed': 0 });
    } catch (err) {
      console.error('Error saving sidebar state:', err);
    }
  }, []);

  // Mark body as the authenticated app shell so global CSS can lock html/body
  // overflow only here. Public landing pages keep normal scrolling.
  useEffect(() => {
    document.body.classList.add('app-shell');
    return () => document.body.classList.remove('app-shell');
  }, []);

  // Section/document/trash/projects/teams state stays reactive via the
  // TanStack Query cache + axios interceptor invalidation. AppLayout's own
  // per-project `sections` map (used to render the sidebar tree) is updated
  // by handleCreateSection / handleRenameSection directly, since each call
  // site already has the affected projectId in scope.

  // Listen for delete project requests from anywhere in the app
  useEffect(() => {
    const handleDeleteProjectRequest = (e) => {
      setProjectToDelete(e.detail.project);
      setShowDeleteModal(true);
    };
    window.addEventListener('deleteProjectRequest', handleDeleteProjectRequest);
    return () => window.removeEventListener('deleteProjectRequest', handleDeleteProjectRequest);
  }, []);

  // Apply sidebar default from Settings page immediately
  useEffect(() => {
    const handler = (e) => setSidebarCollapse(e.detail.value);
    window.addEventListener('sidebarCollapseChanged', handler);
    return () => window.removeEventListener('sidebarCollapseChanged', handler);
  }, []);

  // What New is filled in with, from the screen it is opened on:
  //   - a project's list (?project=&section=) -> that project and list;
  //   - a project, or a chat in one (?project=) -> that project;
  //   - a document (/app/documents/:id) -> the document's project (from the cache; a
  //     document is no task list, so no list);
  //   - anywhere else -> nothing (a task in My Tasks, a chat with BusyBee).
  const newContext = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let projectId = urlParams.get('project');
    let sectionId = urlParams.get('section');
    if (!projectId) {
      const docMatch = window.location.pathname.match(/\/app\/documents\/([^/?#]+)/);
      if (docMatch) {
        const doc = qc.getQueryData(documentKeys.detail(docMatch[1]));
        if (doc?.project_id) { projectId = doc.project_id; sectionId = null; }
      }
    }
    return { projectId: projectId || null, sectionId: projectId ? sectionId || null : null };
  }, [qc]);
  const shellActions = useMemo(() => ({
    openNew: (context) => setNewFor(context || newContext()),
    openSearch: () => setGlobalSearchOpen(true),
  }), [newContext]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K / Ctrl+K: New (the one input and the create tiles), in the project on screen
      // (newContext); again closes it.
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setNewFor((open) => (open ? null : newContext()));
      }
      // Cmd+\ / Ctrl+\ for sidebar toggle
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        toggleSidebarCollapse();
      }
      // Cmd+/ / Ctrl+/ for Global Search
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setGlobalSearchOpen(prev => !prev);
      }
      // Cmd+I / Ctrl+I: a new chat from anywhere, with the default bot (BusyBee; in the project
      // on screen the bot used there last, components/bots/defaultBot.js). In a text editor it
      // stays italic.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'i' && !e.shiftKey && !e.altKey
          && !document.activeElement?.isContentEditable) {
        e.preventDefault();
        navigateRef.current(newChatPath({ projectId: newContext().projectId }));
      }
      // Cmd+Z / Ctrl+Z: undo the last change in the app (a task completed, moved...), except
      // while typing (a chat, a comment, a title, an editor): there it undoes the typing, and
      // a part of the page that undid something itself (a table grid) has said so already.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey && !e.altKey
          && !e.defaultPrevented && !isTypingTarget(e.target)) {
        e.preventDefault();
        triggerUndo();
      }
      // Non-input shortcuts (only active when not typing in an input/textarea)
      const tag = document.activeElement?.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;

      if (!isTyping && !e.metaKey && !e.ctrlKey) {
        // ? for keyboard shortcuts help
        if (e.key === '?') {
          e.preventDefault();
          setShowShortcutsModal(prev => !prev);
        }
        // G then H for go home
        if (e.key === 'g') {
          const waitForNext = (ev) => {
            window.removeEventListener('keydown', waitForNext);
            if (ev.key === 'h') { ev.preventDefault(); navigate('/app'); }
            if (ev.key === 't') { ev.preventDefault(); navigate('/app/tasks'); }
            if (ev.key === 'p') { ev.preventDefault(); navigate('/app/planner'); }
            if (ev.key === 'd') { ev.preventDefault(); navigate('/app/documents'); }
          };
          setTimeout(() => window.removeEventListener('keydown', waitForNext), 1000);
          window.addEventListener('keydown', waitForNext);
        }
      }

      // F1 for Help page
      if (e.key === 'F1') {
        e.preventDefault();
        navigate('/app/help');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebarCollapse, triggerUndo, navigate, newContext]);

  const handleLogout = () => {
    clearUndoStack();
    logout();
  };

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const handleSearchTaskSelect = useCallback((task) => {
    openTaskDetail(task.id || task._id);
  }, [openTaskDetail]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const handleToggleFavorite = useCallback(async (project) => {
    try {
      await projectsApi.update(project.id, { is_favorite: !project.is_favorite });
      fetchSidebarData();
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  }, [fetchSidebarData]);

  const handleProjectContextMenu = useCallback((e, project) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      project
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu({ show: false, x: 0, y: 0, project: null });
  }, []);

  const handleAddProject = useCallback((teamId = null) => {
    const cap = entitlements.limitOf('projects');
    if (cap !== null && projects.length >= cap) {
      window.dispatchEvent(new CustomEvent('upgradNeeded', {
        detail: { resource: 'projects', message: t('ui_pages.limits.projects_limit', { count: cap }) },
      }));
      return;
    }
    setAddProjectForTeam(teamId);
    setShowAddProjectModal(true);
  }, [projects, entitlements, t]);

  const handleProjectCreated = useCallback(() => {
    fetchSidebarData();
    setShowAddProjectModal(false);
    setAddProjectForTeam(null);
  }, [fetchSidebarData]);

  const handleAddToTeam = useCallback((project) => {
    setProjectToAddToTeam(project);
    setShowAddToTeamModal(true);
  }, []);

  const closeAddProject = useCallback(() => { setShowAddProjectModal(false); setAddProjectForTeam(null); }, []);
  const closeAddToTeam = useCallback(() => { setShowAddToTeamModal(false); setProjectToAddToTeam(null); }, []);
  const closeShareProject = useCallback(() => setShareModalProject(null), []);
  const closeActivityLog = useCallback(() => setActivityLogProject(null), []);
  const refetchSettings = settingsQuery.refetch;
  const closeOnboarding = useCallback(() => refetchSettings(), [refetchSettings]);

  const handleAddToTeamSuccess = useCallback(() => {
    fetchSidebarData();
    setShowAddToTeamModal(false);
    setProjectToAddToTeam(null);
  }, [fetchSidebarData]);

  // Sort by custom order, then alphabetical
  const sortByOrder = useCallback((a, b) =>
    a.order !== b.order ? a.order - b.order : a.name.localeCompare(b.name), []);

  // Apply per-user saved ordering for a section, fall back to project.order field
  const applyUserOrder = useCallback((projectList, sectionKey) => {
    const order = userProjectOrders[sectionKey];
    if (!order?.length) return [...projectList].sort(sortByOrder);
    return [...projectList].sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      const ap = ai === -1 ? 9999 : ai;
      const bp = bi === -1 ? 9999 : bi;
      return ap !== bp ? ap - bp : a.name.localeCompare(b.name);
    });
  }, [userProjectOrders, sortByOrder]);

  // All favorited top-level projects (personal + team) shown in Favorites section
  const sidebarFavorites = useMemo(() =>
    applyUserOrder(projects.filter(p => p.is_favorite && !p.parent_id), 'favorites'),
    [projects, applyUserOrder]);

  // "Projects": the ones opened lately that are not favorites, personal and team alike (a team
  // project names its team), newest first; every project is on the Projects page.
  const recentProjects = useMemo(() =>
    projects.filter(p => !p.is_favorite && !p.parent_id)
      .sort((a, b) => String(b.last_accessed_at || b.created_at || '').localeCompare(String(a.last_accessed_at || a.created_at || '')))
      .slice(0, RECENT_PROJECTS),
    [projects]);

  // Children of a project in the sidebar
  const getSidebarChildren = useCallback((parentId) =>
    projects.filter(p => p.parent_id === parentId).sort(sortByOrder),
    [projects, sortByOrder]);

  // Move a project up or down within its section - persists to per-user settings
  const moveProject = useCallback(async (sectionProjects, projectId, direction, sectionKey) => {
    const idx = sectionProjects.findIndex(p => p.id === projectId);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sectionProjects.length) return;
    const reordered = [...sectionProjects];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    const orderedIds = reordered.map(p => p.id);
    setUserProjectOrders(prev => ({ ...prev, [sectionKey]: orderedIds }));
    try {
      await settingsApi.update({ [`ui_preferences.project_orders.${sectionKey}`]: orderedIds });
    } catch {
      fetchSidebarData();
    }
  }, [fetchSidebarData]);

  // Handle project drag end - reorder within its section and persist to per-user settings
  const handleProjectDragEnd = useCallback(async (event, sectionProjects, sectionKey) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sectionProjects.findIndex(p => p.id === active.id);
    const newIdx = sectionProjects.findIndex(p => p.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove([...sectionProjects], oldIdx, newIdx);
    const orderedIds = reordered.map(p => p.id);
    setUserProjectOrders(prev => ({ ...prev, [sectionKey]: orderedIds }));
    try {
      await settingsApi.update({ [`ui_preferences.project_orders.${sectionKey}`]: orderedIds });
    } catch {
      fetchSidebarData();
    }
  }, [fetchSidebarData]);

  // Sort a section A-Z or Z-A and persist to per-user settings
  const sortSection = useCallback(async (sectionKey, sectionProjects, direction) => {
    const sorted = [...sectionProjects].sort((a, b) =>
      direction === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
    );
    const orderedIds = sorted.map(p => p.id);
    setUserProjectOrders(prev => ({ ...prev, [sectionKey]: orderedIds }));
    setSectionSortOrders(prev => ({ ...prev, [sectionKey]: direction }));
    try {
      await settingsApi.update({ [`ui_preferences.project_orders.${sectionKey}`]: orderedIds });
    } catch {
      fetchSidebarData();
    }
  }, [fetchSidebarData]);



  // Active project id (from /app/projects?project=...) so the sidebar can
  // highlight the matching row the same way main-nav items light up.
  const activeProjectId = useMemo(() => {
    if (location.pathname !== '/app/projects') return null;
    return new URLSearchParams(location.search).get('project');
  }, [location.pathname, location.search]);
  // The project of the chat on screen: its row opens so the chat shows under it. The chat is
  // loaded by the chat itself (BotPage); this only reads it.
  const { data: chatOnScreen } = useBotChatQuery(chatIdFromUrl(location.pathname), { enabled: false });
  const chatProjectId = chatOnScreen?.chat?.project_id || null;
  // The document on screen (read from the cache the document page fills): the sidebar opens
  // its project and the folders above it and brings its row into view, the way an editor's
  // file tree reveals the open file. The parents of a sub-project open too.
  const docIdOnScreen = useMemo(() => location.pathname.match(/^\/app\/documents\/([^/]+)$/)?.[1] || null,
    [location.pathname]);
  const { data: docOnScreen } = useDocumentQuery(docIdOnScreen, { enabled: false });
  const docProjectId = (docOnScreen && docOnScreen.id === docIdOnScreen && docOnScreen.project_id) || null;
  // Fetch sections for a project when expanded
  const loadProjectSections = useCallback(async (projectId) => {
    if (sections[projectId]) return;
    try {
      const res = await projectsApi.getSections(projectId);
      setSections(prev => ({ ...prev, [projectId]: res.data || [] }));
    } catch { /* ignore */ }
  }, [sections]);
  // Opening a project (or its chat) expands its row, once per navigation: a row the person
  // folded stays folded when its sections change (so the loader goes through a ref).
  const loadSectionsRef = useRef(loadProjectSections);
  loadSectionsRef.current = loadProjectSections;
  useEffect(() => {
    const id = activeProjectId || chatProjectId || docProjectId;
    if (!id) return;
    setExpandedSections((prev) => (prev[`proj_${id}`] ? prev : { ...prev, [`proj_${id}`]: true }));
    loadSectionsRef.current(id);  // nothing when they are loaded already
  }, [activeProjectId, chatProjectId, docProjectId]);
  // An open document: its project's parents open too, and so do the folders above it. A
  // project that is neither a favourite nor among the recent ones joins the recent ones (it was
  // just opened, as when it is opened from the Projects page), so its row is there to show.
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const documentsRef = useRef(documents);
  documentsRef.current = documents;
  const docsLoaded = documents.length > 0;
  useEffect(() => {
    if (!docIdOnScreen || !docProjectId) return;
    const byId = new Map(projectsRef.current.map((p) => [p.id, p]));
    const parents = [];
    for (let p = byId.get(docProjectId)?.parent_id, guard = 0; p && guard < 20; guard += 1) {
      parents.push(p);
      p = byId.get(p)?.parent_id;
    }
    if (parents.length) {
      setExpandedSections((prev) => {
        const next = { ...prev };
        parents.forEach((pid) => { next[`proj_${pid}`] = true; });
        return next;
      });
    }
    const docs = new Map(documentsRef.current.map((d) => [d.id, d]));
    const folders = [];
    for (let f = docs.get(docIdOnScreen)?.parent_id, guard = 0; f && guard < 50; guard += 1) {
      folders.push(f);
      f = docs.get(f)?.parent_id;
    }
    if (folders.length) {
      setExpandedDocs((prev) => (folders.every((f) => prev.has(f)) ? prev : new Set([...prev, ...folders])));
    }
    projectsApi.access(docProjectId).catch(() => {});
    const top = parents[parents.length - 1] || docProjectId;
    const at = new Date().toISOString();
    qc.setQueryData(projectKeys.list(), (old) => (Array.isArray(old)
      ? old.map((p) => (p.id === top && !p.is_favorite ? { ...p, last_accessed_at: at } : p)) : old));
  }, [docIdOnScreen, docProjectId, docsLoaded, qc]);
  // ... and brings it into view in the sidebar (to the middle when it is off screen): its row
  // among the favorites when it is one (they come first), else in its section. Again once
  // the projects arrive, if they were still loading. Moving to another project greets its row.
  const projectsLoaded = projects.length > 0;
  const greetedProjectRef = useRef(null);
  useEffect(() => {
    const id = activeProjectId || chatProjectId;
    if (!id || !projectsLoaded) return undefined;
    const timer = setTimeout(() => {
      revealSidebarProject(id, { greet: greetedProjectRef.current !== id });
      greetedProjectRef.current = id;
    }, 60);  // after the row (and its expanded content) is drawn
    return () => clearTimeout(timer);
  }, [activeProjectId, chatProjectId, projectsLoaded]);
  // The open document's row, once its project and folders are open (without the greeting:
  // the row is lit as the open one, and documents are opened from the sidebar itself often).
  useEffect(() => {
    if (!docIdOnScreen || !docProjectId || !projectsLoaded || !docsLoaded) return undefined;
    const timer = setTimeout(() => revealSidebarDocument(docIdOnScreen, { greet: false }), 120);
    return () => clearTimeout(timer);
  }, [docIdOnScreen, docProjectId, projectsLoaded, docsLoaded]);
  const navActive = (item) => item.match(location.pathname, location.search);
  // What a place's row says: Tasks how many wait to be planned, Chats what waits for the
  // person (orange) or how many chats have something unread.
  const navBadge = (item) => {
    if (item.count === 'inbox' && inboxCount > 0) return <CountPill>{inboxCount > 99 ? '99+' : inboxCount}</CountPill>;
    if (item.count === 'chats' && botsWaiting > 0) return <AttentionPill title={t('bots.state.needs_you')}>{botsWaiting}</AttentionPill>;
    if (item.count === 'chats' && botsUnread > 0) return <CountPill>{botsUnread}</CountPill>;
    return null;
  };

  // Render collapsed sidebar (icons only): New, Search, the four places, then the projects
  // the full sidebar lists (favorites, then the ones opened lately) as their tiles.
  const renderCollapsedSidebar = (onItemClick) => (
    <div className="flex flex-col h-full">
      <div className="px-2 pt-2 flex flex-col items-center gap-1">
        <button type="button" onClick={() => { setNewFor(newContext()); onItemClick?.(); }}
          title={`${t('nav.create.new')} (${newShortcutLabel})`} aria-label={t('nav.create.new')} data-testid="sidebar-new"
          className="w-9 h-9 rounded-lg flex items-center justify-center border border-border bg-card text-foreground hover:border-input">
          <Plus className="w-[17px] h-[17px]" />
        </button>
        <button type="button" onClick={() => setGlobalSearchOpen(true)} title={`${t('nav.search')} (${shortcutLabel})`} aria-label={t('nav.search')}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent">
          <Search className="w-[17px] h-[17px]" />
        </button>
      </div>

      <nav className="px-2 py-2 flex flex-col items-center gap-1" aria-label={t('nav.home')}>
        {mainNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = navActive(item);
          return (
            <Link key={item.path} to={item.path} onClick={onItemClick} title={item.name} aria-label={item.name}
              className={`relative w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                isActive ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}>
              <Icon className="w-[17px] h-[17px] text-brand-icon" strokeWidth={1.75} />
              {item.count === 'inbox' && inboxCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              )}
              {item.count === 'chats' && (botsWaiting > 0 ? (
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-attention" />
              ) : botsUnread > 0 && <ChatUnreadDot className="absolute top-1.5 right-1.5" />)}
            </Link>
          );
        })}
      </nav>

      {(() => {
        const seen = new Set();
        const collapsedProjects = [...sidebarFavorites, ...recentProjects].filter((p) => !seen.has(p.id) && seen.add(p.id));
        if (collapsedProjects.length === 0) return null;
        return (
          <>
          <div className="mx-auto w-6 border-t border-border my-1" />
          <nav className="px-2 py-1 flex flex-col items-center gap-1" aria-label={t('nav.projects_section')}>
            {collapsedProjects.map((project) => (
              <Link
                key={project.id}
                to={`/app/projects?project=${project.id}`}
                onClick={() => { onItemClick?.(); projectsApi.access(project.id).catch(() => {}); }}
                title={project.name}
                aria-label={project.name}
                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                  activeProjectId === project.id ? 'bg-accent' : 'hover:bg-accent'
                }`}
              >
                <span className="relative inline-flex">
                  <ProjectTile project={project} size={22} />
                  <ProjectUnreadDot projectId={project.id} className="absolute -top-1 -right-1.5" />
                </span>
              </Link>
            ))}
          </nav>
          </>
        );
      })()}

    </div>
  );

  // Create a new document inside a project (optionally nested under a folder)
  const handleCreateDocument = async (projectId, type = 'document', parentId = null) => {
    const docCap = entitlements.limitOf('documents');
    if (docCap !== null && documents.length >= docCap) {
      window.dispatchEvent(new CustomEvent('upgradNeeded', {
        detail: { resource: 'documents', message: t('ui_pages.limits.documents_limit', { count: docCap }) },
      }));
      return;
    }
    try {
      // No title yet: every reader sees "Untitled" in their own language.
      const res = await documentsApi.create({
        title: '', type,
        content: type === 'document' ? '' : null,
        project_id: projectId,
        parent_id: parentId,
      });
      const doc = res.data;
      // Optimistically append the new doc to every cached document list.
      qc.getQueriesData({ queryKey: ['documents', 'list'] }).forEach(([key, list]) => {
        if (Array.isArray(list)) qc.setQueryData(key, [...list, doc]);
      });
      // Auto-expand the parent folder so the new child is visible immediately.
      if (parentId) {
        setExpandedDocs(prev => {
          const next = new Set(prev);
          next.add(parentId);
          return next;
        });
      }
      navigate(`/app/documents/${doc.id}`, { state: { nameIt: true } });
    } catch (err) {
      console.error('Failed to create document:', err);
    }
  };

  // Get documents for a specific project
  const getProjectDocs = (projectId) => documents.filter(d => d.project_id === projectId);
  const getProjectSections = (projectId) => sections[projectId] || [];

  // Create a new section (list) in a project
  const handleCreateSection = async (projectId) => {
    try {
      const res = await projectsApi.createSection(projectId, { name: t('ui_pages.projects.new_list') });
      setSections(prev => ({ ...prev, [projectId]: [...(prev[projectId] || []), res.data] }));
      // Auto-expand the project so the new list is visible
      setExpandedSections(prev => ({ ...prev, [`proj_${projectId}`]: true }));
      // ...with its name being typed (the default selected), instead of opening and renaming it.
      setRenameValue(res.data.name);
      setRenamingItem({ type: 'section', id: res.data.id });
      // Sync the cached section query so other components see the new list.
      qc.invalidateQueries({ queryKey: projectKeys.sections(projectId) });
      setAddMenu(m => m.show ? { show: false, x: 0, y: 0, projectId: null, parentId: null } : m);
    } catch (err) {
      console.error('Failed to create section:', err);
    }
  };

  // New's actions that leave it (components/create/NewPanel.jsx). A chat in a project opens in
  // its chat panel (with the bot used there last); elsewhere it is a new chat with BusyBee.
  const newProject = useMemo(() => (newFor?.projectId ? projects.find((p) => p.id === newFor.projectId) || null : null),
    [newFor, projects]);
  const closeNew = useCallback((open) => { if (!open) setNewFor(null); }, []);
  const startChat = useCallback((project) => {
    setMobileMenuOpen(false);
    navigate(project ? `/app/projects?project=${project.id}&panel=chat` : newChatPath());
  }, [navigate]);
  const addProjectFromNew = useCallback(() => handleAddProject(null), [handleAddProject]);

  // Item context menu (sections & documents)
  const openItemMenu = useCallback((e, type, item, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    setItemMenu({ show: true, x: e.clientX, y: e.clientY, type, item, projectId });
  }, []);

  const closeItemMenu = useCallback(() => {
    setItemMenu({ show: false, x: 0, y: 0, type: null, item: null, projectId: null });
  }, []);

  const handleDeleteSection = useCallback(async (section, projectId) => {
    try {
      await projectsApi.deleteSection(section.id);
      setSections(prev => ({
        ...prev,
        [projectId]: (prev[projectId] || []).filter(s => s.id !== section.id)
      }));
      pushUndo({
        label: t('projects.list_deleted', { name: section.name }),
        undo: async () => {
          // Back from the Trash with its own id (and position).
          await trashApi.restoreSection(section.id);
          setSections(prev => ({ ...prev, [projectId]: [...(prev[projectId] || []), section] }));
        },
      });
    } catch (err) {
      console.error('Failed to delete section:', err);
    }
  }, [pushUndo, t]);

  const handleDeleteDocument = useCallback(async (doc) => {
    try {
      await documentsApi.delete(doc.id);
      // Interceptor invalidates ['documents'] anyway; the optimistic snip
      // here makes the row disappear without a flash of the post-refetch list.
      qc.getQueriesData({ queryKey: ['documents', 'list'] }).forEach(([key, list]) => {
        if (Array.isArray(list)) qc.setQueryData(key, list.filter(d => d.id !== doc.id));
      });
      pushUndo({
        label: t('documents.moved_to_trash', { title: doc.title || t('documents.untitled') }),
        undo: async () => {
          await trashApi.restoreDocument(doc.id);
          qc.invalidateQueries({ queryKey: ['documents'] });
        },
      });
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  }, [pushUndo, qc, t]);

  const handleRenameSection = useCallback(async (sectionId, newName, projectId) => {
    if (!newName.trim()) return;
    try {
      await projectsApi.updateSection(sectionId, { name: newName.trim() });
      setSections(prev => ({
        ...prev,
        [projectId]: (prev[projectId] || []).map(s => s.id === sectionId ? { ...s, name: newName.trim() } : s)
      }));
      // Keep the open ProjectsPage (which reads sections from React Query) in sync.
      window.dispatchEvent(new CustomEvent('sectionRenamed', {
        detail: { sectionId, projectId, name: newName.trim() },
      }));
    } catch (err) {
      console.error('Failed to rename section:', err);
    }
    setRenamingItem(null);
  }, []);

  // Reflect section renames done elsewhere (e.g. ProjectsPage) in the sidebar's
  // own (non-RQ) section state, so they don't require a page refresh.
  useEffect(() => {
    const handler = (e) => {
      const { sectionId, projectId, name } = e.detail || {};
      if (!sectionId || !projectId) return;
      setSections(prev => prev[projectId]
        ? { ...prev, [projectId]: prev[projectId].map(s => s.id === sectionId ? { ...s, name } : s) }
        : prev);
    };
    window.addEventListener('sectionRenamed', handler);
    return () => window.removeEventListener('sectionRenamed', handler);
  }, []);

  const handleRenameDocument = useCallback(async (docId, newTitle) => {
    if (!newTitle.trim()) return;
    try {
      await documentsApi.update(docId, { title: newTitle.trim() });
      // Optimistically patch every cached document list.
      qc.getQueriesData({ queryKey: ['documents', 'list'] }).forEach(([key, list]) => {
        if (Array.isArray(list))
          qc.setQueryData(key, list.map(d => d.id === docId ? { ...d, title: newTitle.trim() } : d));
      });
    } catch (err) {
      console.error('Failed to rename document:', err);
    }
    setRenamingItem(null);
  }, [qc]);

  // Viewport adjustment for item menu
  useEffect(() => {
    if (itemMenu.show && itemMenuRef.current) {
      const rect = itemMenuRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = itemMenu.x;
      let top = itemMenu.y;
      if (rect.right > vw) left = itemMenu.x - rect.width;
      if (rect.bottom > vh) top = itemMenu.y - rect.height;
      left = Math.max(8, left);
      top = Math.max(8, top);
      itemMenuRef.current.style.left = `${left}px`;
      itemMenuRef.current.style.top = `${top}px`;
    }
  }, [itemMenu]);

  // Toggle project expand and load sections
  const handleToggleProject = (projectId) => {
    const key = `proj_${projectId}`;
    const willExpand = !expandedSections[key];
    toggleSection(key);
    if (willExpand) loadProjectSections(projectId);
  };

  // Render a project item with nested children (ClickUp-style)
  const renderProjectItem = (project, { onItemClick, showTeam = false } = {}) => {
    const projectDocs = getProjectDocs(project.id);
    const projectSections = getProjectSections(project.id);
    // BG_76: child projects (parent_id === this project) must surface in the
    // sidebar hierarchy too. They are filtered out of the top-level lists, so
    // without rendering them here a project that gets a parent disappears from
    // the sidebar entirely. Render them nested under their parent, recursively.
    const childProjects = getSidebarChildren(project.id);
    const isExpanded = expandedSections[`proj_${project.id}`];
    const isHovered = hoveredProjectId === project.id || addMenu.projectId === project.id;
    const isActive = activeProjectId === project.id;
    // A project is "expandable" when it has at least one child row to show:
    // a section, a root document, or (server-reported) section_count > 0.
    // We rely on `section_count` from the backend so we don't have to fetch
    // sections for every project just to decide whether to render a chevron.
    const chatInfo = chatCounts[project.id] || { chats: 0, needs_you: 0 };
    // Lists, documents and child projects are the project's content; its chats
    // fold into one "Chats" row next to them, or show right away when the
    // project has nothing else (no second click to reach them).
    const hasContent =
      (project.section_count || 0) > 0 ||
      projectSections.length > 0 ||
      projectDocs.length > 0 ||
      childProjects.length > 0;
    const hasSubItems = hasContent || chatInfo.chats > 0;

    return (
      <div key={project.id}>
        <div
          data-sidebar-project={project.id}
          className={`relative flex items-center h-[30px] rounded-lg group/proj transition-colors ${
            isActive ? 'bg-accent' : 'hover:bg-accent/60'
          }`}
          onMouseEnter={() => setHoveredProjectId(project.id)}
          onMouseLeave={() => setHoveredProjectId(null)}
        >
          {/* The project's tile sits in the row's icon slot; on hover it turns
              into the expand chevron (when there is something to expand). */}
          {hasSubItems ? (
            <button
              onClick={() => handleToggleProject(project.id)}
              aria-label={isExpanded ? t('nav.collapse') : t('nav.expand')}
              aria-expanded={!!isExpanded}
              className="ml-2 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
            >
              <span className={isExpanded ? 'hidden' : 'group-hover/proj:hidden'}><ProjectDot project={project} /></span>
              <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-150 ${isExpanded ? 'rotate-90' : 'hidden group-hover/proj:block'}`} />
            </button>
          ) : (
            <span className="ml-2 w-5 h-5 flex-shrink-0 flex items-center justify-center" aria-hidden="true"><ProjectDot project={project} /></span>
          )}
          <Link
            to={`/app/projects?project=${project.id}`}
            // A team project shows its team on hover (Favorites mix teams), as Slack shows a
            // shared channel's workspaces: never as a label in the row.
            title={project.team_id ? [project.name, teams.find((tm) => tm.id === project.team_id)?.name].filter(Boolean).join(' · ') : undefined}
            onClick={() => {
              onItemClick?.();
              projectsApi.access(project.id).catch(() => {});
              // Auto-expand on click so the user can see the project's
              // sections / documents without a second click on the chevron -
              // but only when there's actually something to expand.
              if (hasSubItems && !isExpanded) handleToggleProject(project.id);
            }}
            className={`flex items-center gap-2 flex-1 h-full pl-2.5 ${isHovered || isMobile ? 'pr-12' : 'pr-6'} transition-colors min-w-0 ${
              isActive ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {/* Among favorites a team project names its team after it, muted, as a team bot
                does (BotName); in its team's section the header already says it. */}
            <span className="text-[13px] truncate">
              {project.name}
              {showTeam && project.team_id && project.team_name && (
                <span className={teamSuffixClass}> · {project.team_name}</span>
              )}
            </span>
            {!isExpanded && (chatInfo.needs_you > 0 ? (
              <span className="w-1.5 h-1.5 rounded-full bg-attention flex-shrink-0" aria-label={t('bots.state.needs_you')} />
            ) : chatInfo.unread > 0 && <ChatUnreadDot />)}
            {/* While the + and ... buttons show over the right edge, the row makes
                room for them (pr-12), so the dot and the name stay clear of them. */}
            <ProjectUnreadDot projectId={project.id} className="ml-auto flex-shrink-0" />
          </Link>
          {(isHovered || isMobile) && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Anchor the menu to the trigger button rather than the
                  // raw click point - that gives us a stable rect to flip
                  // around when there isn't enough space below.
                  const rect = e.currentTarget.getBoundingClientRect();
                  const MENU_HEIGHT = 230;  // the New menu: tiles and document chips
                  const MENU_WIDTH = 380;   // matches CreateMenu's width
                  const margin = 8;
                  const spaceBelow = window.innerHeight - rect.bottom;
                  const top = spaceBelow >= MENU_HEIGHT + margin
                    ? rect.bottom + 4                          // open downward
                    : Math.max(margin, rect.top - MENU_HEIGHT - 4);  // flip up
                  // Clamp horizontally too so menus near the right edge stay visible.
                  const left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - margin);
                  setAddMenu({ show: true, x: left, y: top, projectId: project.id });
                }}
                className="p-0.5 hover:bg-accent rounded text-muted-foreground/60 hover:text-foreground"
                title={t('nav.create.new_in', { name: project.name })}
                aria-label={t('nav.create.new_in', { name: project.name })}
                data-testid="project-add-menu"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
              <button onClick={(e) => handleProjectContextMenu(e, project)} className="p-0.5 hover:bg-accent rounded text-muted-foreground/60 hover:text-foreground">
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
        {isExpanded && (
          <div className="ml-[17px] border-l border-border pl-1.5 space-y-px py-0.5">
            {/* BG_76: nested child projects first, then this project's own
                sections and documents. Recursing keeps grandchildren (and
                deeper) visible too. */}
            {childProjects.map(child => renderProjectItem(child, { onItemClick }))}
            {/* Sections (Lists) — no leading spacer. The chevron on folder
                rows below is positioned ABSOLUTELY in the parent gutter
                (see renderDoc), so every row's icon/title sits at the same
                horizontal position regardless of whether it's a section, a
                folder, or a leaf document. */}
            {projectSections.map(section => (
              <div
                key={section.id}
                className="relative flex items-center rounded-md group/item hover:bg-accent"
                onMouseEnter={() => setHoveredItemId(`s_${section.id}`)}
                onMouseLeave={() => setHoveredItemId(null)}
              >
                {renamingItem?.type === 'section' && renamingItem?.id === section.id ? (
                  <div className="flex items-center gap-2 px-2 py-1 flex-1 min-w-0">
                    <ListChecks className="w-3.5 h-3.5 flex-shrink-0 text-brand-icon" />
                    <input
                      autoFocus
                      onFocus={(e) => e.target.select()}
                      className="flex-1 bg-transparent text-[13px] text-foreground outline-none border-b border-primary min-w-0"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleRenameSection(section.id, renameValue, project.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSection(section.id, renameValue, project.id);
                        if (e.key === 'Escape') setRenamingItem(null);
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <Link
                      to={`/app/projects?project=${project.id}&section=${section.id}`}
                      onClick={onItemClick}
                      className={`flex items-center gap-2 px-2 py-1 flex-1 rounded-md text-[13px] transition-colors min-w-0 ${
                        location.pathname === '/app/projects' && new URLSearchParams(location.search).get('section') === section.id
                          ? 'bg-accent text-foreground font-medium'
                          : 'text-foreground/80 hover:text-foreground'
                      }`}
                    >
                      <ListChecks className="w-3.5 h-3.5 flex-shrink-0 text-brand-icon" />
                      <span className="truncate">{section.name}</span>
                    </Link>
                    {(isMobile || hoveredItemId === `s_${section.id}`) && (
                      <button
                        onClick={(e) => openItemMenu(e, 'section', section, project.id)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 hover:bg-accent rounded text-muted-foreground/60 hover:text-foreground"
                      >
                        <MoreHorizontal className="w-3 h-3" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
            {/* Documents - root level + nested folder children. Folders show
                a chevron toggle; expanding renders children indented one
                level deeper. Children which are themselves folders nest
                arbitrarily through the same recursion. */}
            {(() => {
              const docIcon = (type) => (
                type === 'folder' ? Folder :
                type === 'database' ? Database :
                type === 'whiteboard' ? Presentation :
                type === 'mindmap' ? Network :
                type === 'embed' ? Globe :
                type === 'changelog' ? History :
                FileText
              );
              const docColor = () => 'text-brand-icon';
              const renderDoc = (doc, depth) => {
                const docType = doc.type || 'document';
                const DocIcon = docIcon(docType);
                const iconColor = docColor(docType);
                const isFolder = docType === 'folder';
                const isExpanded = expandedDocs.has(doc.id);
                const childDocs = isFolder
                  ? projectDocs.filter(d => d.parent_id === doc.id)
                  : [];
                const toggleExpand = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExpandedDocs(prev => {
                    const next = new Set(prev);
                    if (next.has(doc.id)) next.delete(doc.id);
                    else next.add(doc.id);
                    return next;
                  });
                };
                return (
                  <React.Fragment key={doc.id}>
                    <div
                      data-sidebar-doc={doc.id}
                      className="relative flex items-center rounded-md group/item hover:bg-accent"
                      onMouseEnter={() => setHoveredItemId(`d_${doc.id}`)}
                      onMouseLeave={() => setHoveredItemId(null)}
                      style={{ paddingLeft: depth * 12 }}
                    >
                      {/* Folder chevron — absolutely positioned in the
                          parent's left gutter so it doesn't push the icon /
                          title rightwards. Every row (section, folder,
                          leaf doc) shares the SAME inline content layout;
                          only folders show this chevron, sitting just
                          outside the row's left edge. */}
                      {isFolder && (
                        <button
                          type="button"
                          onClick={toggleExpand}
                          className="absolute -left-3 top-1/2 -translate-y-1/2 w-3 h-3 flex items-center justify-center text-muted-foreground/50 hover:text-foreground"
                          aria-label={isExpanded ? t('nav.collapse') : t('nav.expand')}
                        >
                          <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                      )}
                      {renamingItem?.type === 'document' && renamingItem?.id === doc.id ? (
                        <div className="flex items-center gap-2 px-2 py-1 flex-1 min-w-0">
                          <DocIcon className={`w-3.5 h-3.5 flex-shrink-0 ${iconColor}`} />
                          <input
                            autoFocus
                            onFocus={(e) => e.target.select()}
                            className="flex-1 bg-transparent text-[13px] text-foreground outline-none border-b border-primary min-w-0"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => handleRenameDocument(doc.id, renameValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameDocument(doc.id, renameValue);
                              if (e.key === 'Escape') setRenamingItem(null);
                            }}
                          />
                        </div>
                      ) : (
                        <>
                          <Link
                            to={`/app/documents/${doc.id}`}
                            onClick={() => {
                              onItemClick?.();
                              // Auto-expand folders on click, matching the
                              // project-row behaviour above — so the user
                              // immediately sees children when navigating in.
                              if (isFolder && !isExpanded) {
                                setExpandedDocs(prev => {
                                  const next = new Set(prev);
                                  next.add(doc.id);
                                  return next;
                                });
                              }
                            }}
                            className={`flex items-center gap-2 px-2 py-1 flex-1 rounded-md text-[13px] transition-colors min-w-0 ${
                              location.pathname === `/app/documents/${doc.id}`
                                ? 'bg-accent text-foreground font-medium'
                                : 'text-foreground/80 hover:text-foreground'
                            }`}
                          >
                            <DocIcon className={`w-3.5 h-3.5 flex-shrink-0 ${iconColor}`} />
                            <span className="truncate">{doc.title || t('documents.untitled')}</span>
                          </Link>
                          {(isMobile || hoveredItemId === `d_${doc.id}`) && (
                            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                              {isFolder && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const MENU_HEIGHT = 230;
                                    const MENU_WIDTH = 380;
                                    const margin = 8;
                                    const spaceBelow = window.innerHeight - rect.bottom;
                                    const top = spaceBelow >= MENU_HEIGHT + margin
                                      ? rect.bottom + 4
                                      : Math.max(margin, rect.top - MENU_HEIGHT - 4);
                                    const left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - margin);
                                    setAddMenu({ show: true, x: left, y: top, projectId: project.id, parentId: doc.id });
                                  }}
                                  className="p-0.5 hover:bg-accent rounded text-muted-foreground/60 hover:text-foreground"
                                  title={t('ui_pages.layout.add_to_folder')}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              )}
                              <button
                                onClick={(e) => openItemMenu(e, 'document', doc, project.id)}
                                className="p-0.5 hover:bg-accent rounded text-muted-foreground/60 hover:text-foreground"
                              >
                                <MoreHorizontal className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {isFolder && isExpanded && childDocs.map(child => renderDoc(child, depth + 1))}
                  </React.Fragment>
                );
              };
              // Documents can only nest inside a FOLDER document. If a doc's
              // parent_id doesn't point to an existing folder, treat it as
              // root so the row still surfaces in the sidebar rather than
              // hiding under an invalid parent.
              const docById = new Map(projectDocs.map(d => [d.id, d]));
              const rootDocs = projectDocs.filter(d => {
                if (!d.parent_id) return true;
                const parent = docById.get(d.parent_id);
                return !parent || parent.type !== 'folder';
              });
              return rootDocs.map(d => renderDoc(d, 0));
            })()}
            {chatInfo.chats > 0 && (
              <SidebarProjectChats project={project} grouped={hasContent} needsYou={chatInfo.needs_you} unread={chatInfo.unread || 0} onItemClick={onItemClick} />
            )}
            {!hasContent && chatInfo.chats === 0 && (
              <p className="px-2 py-1 text-[11px] text-muted-foreground/30">{t('common.none')}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render sidebar content
  // "More": the pages people use less often (the old header icons), one click away.
  const renderMoreSection = (onItemClick) => (
    <div className="flex flex-col gap-px">
      <button type="button" onClick={() => setMoreOpen((o) => !o)} aria-expanded={moreOpen}
        className={rowClass(false, 'w-full text-left')} data-testid="sidebar-more">
        <IconSlot><MoreHorizontal className="w-4 h-4" strokeWidth={1.75} /></IconSlot>
        <span className="flex-1">{t('nav.more')}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
      </button>
      {moreOpen && headerToolItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        const locked = item.gatedFeature && !has(item.gatedFeature);
        const body = (
          <>
            <IconSlot><Icon className="w-4 h-4" strokeWidth={1.75} /></IconSlot>
            <span className="flex-1 truncate">{item.name}</span>
            {item.path === '/app/trash' && trashCount > 0 && <CountPill>{trashCount}</CountPill>}
          </>
        );
        return locked ? (
          <button key={item.path} type="button" className={rowClass(false, 'w-full text-left opacity-70')}
            title={`${item.name} - ${t('nav.needs_higher_plan')}`}
            onClick={() => window.dispatchEvent(new CustomEvent('upgradNeeded', {
              detail: { feature: item.gatedFeature, message: t('ui_pages.limits.requires_higher_plan', { name: item.name }) },
            }))}>
            {body}
          </button>
        ) : (
          <Link key={item.path} to={item.path} onClick={onItemClick} className={rowClass(isActive)}>{body}</Link>
        );
      })}
    </div>
  );

  const renderSidebarContent = (onItemClick) => (
    <div className="flex flex-col gap-4 px-2.5 pt-1">
      <nav className="flex flex-col gap-px">
        {mainNavItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.path} to={item.path} onClick={onItemClick} className={rowClass(navActive(item))} data-testid={item.testId}>
              <IconSlot><Icon className="w-4 h-4 text-brand-icon" strokeWidth={1.75} /></IconSlot>
              <span className="flex-1 truncate">{item.name}</span>
              {navBadge(item)}
            </Link>
          );
        })}
      </nav>

      {/* The latest chats with any bot; "All chats" is the one history. */}
      <SidebarRecentChats onItemClick={onItemClick} />

      {/* Favorites */}
      {sidebarFavorites.length > 0 && (() => {
        const favSortOrder = sectionSortOrders['favorites'];
        const nextFavDir = favSortOrder === 'asc' ? 'desc' : 'asc';
        const FavSortIcon = nextFavDir === 'asc' ? ArrowDownAZ : ArrowUpAZ;
        return (
          <div
            onMouseEnter={() => setFavoritesHovered(true)}
            onMouseLeave={() => setFavoritesHovered(false)}
          >
            <SectionHeader actions={(
              <SectionButton visible={isMobile || favoritesHovered} title={nextFavDir === 'asc' ? t('nav.sort_az') : t('nav.sort_za')}
                onClick={(e) => { e.stopPropagation(); sortSection('favorites', sidebarFavorites, nextFavDir); }}>
                <FavSortIcon className="w-3 h-3" />
              </SectionButton>
            )}>{t('nav.favorites')}</SectionHeader>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleProjectDragEnd(e, sidebarFavorites, 'favorites')}>
              <SortableContext items={sidebarFavorites.map(p => p.id)} strategy={verticalListSortingStrategy}>
                {sidebarFavorites.map((project) => (
                  <SortableProjectRow key={project.id} id={project.id}>
                    {renderProjectItem(project, { onItemClick, showTeam: true })}
                  </SortableProjectRow>
                ))}
              </SortableContext>
            </DndContext>
          </div>
        );
      })()}

      {/* Projects: the ones opened lately that are not favorites (a team project names its
          team, never a second list per team), then all of them on the Projects page. */}
      <div onMouseEnter={() => setProjectsHovered(true)} onMouseLeave={() => setProjectsHovered(false)}
        data-testid="sidebar-projects">
        <SectionHeader actions={(
          <SectionButton visible={isMobile || projectsHovered || projects.length === 0} title={t('nav.add_project')}
            onClick={(e) => { e.stopPropagation(); handleAddProject(null); }}>
            <Plus className="w-3.5 h-3.5" />
          </SectionButton>
        )}>{t('nav.projects_section')}</SectionHeader>
        {projects.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            {t('nav.no_projects', 'No projects yet')}
          </div>
        ) : (
          <div className="flex flex-col gap-px">
            {recentProjects.map((project) => renderProjectItem(project, { onItemClick, showTeam: true }))}
            <Link to="/app/projects" onClick={onItemClick} data-testid="sidebar-all-projects"
              className={rowClass(false, '!text-muted-foreground hover:!text-foreground')}>
              <IconSlot />
              <span className="flex-1 truncate text-[12.5px]">{t('tasks.all_projects')}</span>
            </Link>
          </div>
        )}
      </div>

      {renderMoreSection(onItemClick)}

      {/* Bottom spacer - ensures last project clears the footer. pointer-events-none
          so this purely-visual element never sits in hit-testing: otherwise its
          default `cursor: auto` flanks the last team's action buttons and the
          cursor flickers arrow<->pointer as the mouse moves across them. */}
      <div className="flex-shrink-0 h-4 pointer-events-none" />
    </div>
  );

  // Sidebar footer: plan, bot credits and limits, then the person and their menu.
  const renderPlanFooter = (onItemClick) => {
    const usage = [
      { key: 'projects', icon: Folder, used: projects.length, limit: limitOf('projects') },
      { key: 'documents', icon: FileText, used: documents.length, limit: limitOf('documents') },
    ].filter(r => r.limit != null);
    return (
      <SidebarFooter user={user} plan={plan} isFree={isFree} trialing={user?.subscription_status === 'trialing'}
        usage={usage} onLogout={handleLogout} onShortcuts={() => setShowShortcutsModal(true)} onItemClick={onItemClick} />
    );
  };

  // The top of the sidebar: BusyBee, notifications and collapse; then search and create.
  const renderSidebarHead = (onItemClick, onClose) => (
    <div className="flex-shrink-0 px-2.5 pt-3 pb-3 space-y-3">
      <div className="flex items-center gap-1">
        <Link to="/app" onClick={onItemClick} className="flex-1 min-w-0 flex items-center gap-2 h-9 px-1.5 rounded-lg hover:bg-accent/60">
          <BusyBeeIcon className="w-[22px] h-[22px]" />
          <span className="text-[15px] tracking-tight text-foreground"><span className="font-semibold">Busy</span><span className="text-amber-600 dark:text-amber-400">Bee.Day</span></span>
        </Link>
        <NotificationBell side="right" align="start" className="w-[30px] h-[30px] flex items-center justify-center rounded-lg" />
        {onClose ? (
          <button type="button" onClick={onClose} aria-label={t('common.close')}
            className="w-[30px] h-[30px] flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
            <X className="w-4 h-4" />
          </button>
        ) : (
          <button type="button" onClick={toggleSidebarCollapse} title={t('nav.collapse_sidebar')} aria-label={t('nav.collapse_sidebar')}
            className="w-[30px] h-[30px] flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
            <PanelLeftClose className="w-4 h-4" strokeWidth={1.75} />
          </button>
        )}
      </div>
      {/* Search (Ctrl+/) and the one "+ New" for everything (Ctrl+K) share a row: two rows
          for them pushed the places down for nothing (the user, 2026-10-30). */}
      <div className="flex items-center gap-1.5">
        <button type="button" data-tour="search-trigger" onClick={() => { setGlobalSearchOpen(true); onItemClick?.(); }}
          className="flex-1 min-w-0 h-8 flex items-center gap-2 px-2.5 rounded-lg border border-border bg-card text-[13px] text-muted-foreground hover:text-foreground hover:border-input transition-colors">
          <Search className="w-[15px] h-[15px]" />
          <span className="flex-1 text-left truncate">{t('nav.search')}</span>
          <kbd className="font-mono text-[11px] text-muted-foreground">{shortcutLabel}</kbd>
        </button>
        <button type="button" onClick={() => { setNewFor(newContext()); onItemClick?.(); }} data-testid="sidebar-new"
          title={`${t('nav.create.new')} (${newShortcutLabel})`} aria-label={t('nav.create.new')}
          className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg border border-border bg-card text-foreground hover:border-input transition-colors">
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Sidebar width based on collapse state
  const sidebarWidth = sidebarCollapse === 0 ? 'w-[248px]' : sidebarCollapse === 1 ? 'w-[60px]' : 'w-0';

  return (
    <MobileTitleContext.Provider value={setMobileTitleOverride}>
    <ShellActionsContext.Provider value={shellActions}>
    <NotificationsProvider>
    <BotsProvider>
    <RouteProgressBar />
    <div className="h-screen max-md:h-[100dvh] bg-background flex overflow-hidden">
      {/* Sidebar - Desktop */}
      {desktop && <aside data-tour="sidebar" className={`hidden lg:flex flex-col ${sidebarWidth} flex-shrink-0 bg-background transition-[width] duration-200 overflow-hidden h-screen sticky top-0`}>
        {sidebarCollapse === 0 ? renderSidebarHead() : (
          // The icon rail keeps its head in place, as the full sidebar does: the toggle that
          // opens it again sits where the one that closed it was (Linear, Notion), never at
          // the end of a long list of projects.
          <div className="flex-shrink-0 pt-3 pb-1 flex flex-col items-center gap-1">
            <Link to="/app" aria-label="BusyBee.Day" className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-accent/60">
              <BusyBeeIcon className="w-[22px] h-[22px]" />
            </Link>
            <NotificationBell side="right" align="start" className="w-9 h-9 flex items-center justify-center rounded-lg" />
            <button type="button" onClick={expandSidebar} title={t('nav.expand_sidebar')} aria-label={t('nav.expand_sidebar')}
              data-testid="sidebar-expand"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
              <PanelLeftOpen className="w-[17px] h-[17px]" strokeWidth={1.75} />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col min-h-0">
          {sidebarCollapse === 0 ? renderSidebarContent() : renderCollapsedSidebar()}
        </div>

        {sidebarCollapse === 0 && renderPlanFooter()}
      </aside>}

      {/* Sidebar expand button when fully hidden */}
      {sidebarCollapse === 2 && (
        <button
          onClick={expandSidebar}
          className="hidden lg:flex fixed left-0 top-1/2 -translate-y-1/2 z-40 w-8 h-20 bg-card border border-border border-l-0 rounded-r-xl items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent hover:border-border/70 transition-all shadow-lg hover:shadow-xl hover:w-10"
          title={`${t('nav.expand_sidebar')} (⌘\\)`}
        >
          <ChevronsRight className="w-5 h-5" />
        </button>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 h-screen max-md:h-[100dvh] lg:py-2 lg:pr-2">
        {/* Mobile header (replaces the desktop one below md) */}
        {/* The same bar on every screen but an open chat, which brings its own (BotPage). */}
        {!fullBleed && <MobileHeader
          title={screen.home ? greetingText(t, user) : mobileTitle}
          subtitle={screen.home ? todayText(i18n.language) : null}
          home={screen.home}
          root={screen.root}
          onBackClick={goBack}
          onSearchClick={() => setGlobalSearchOpen(true)}
          onBellClick={() => navigate('/app/notifications')}
          onAvatarClick={() => setMobileMoreOpen(true)}
        />}

        {/* Top bar (tablet only; phones have MobileHeader, desktop the sidebar).
            Everything else lives in the drawer, same as the sidebar. */}
        {tablet && <header className="hidden md:flex lg:hidden h-14 flex-shrink-0 items-center justify-between px-4 sm:px-6 border-b border-border bg-background z-30">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t('nav.expand_sidebar')}
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/app" className="flex items-center gap-2 h-9 px-1.5 rounded-lg hover:bg-accent/60">
              <BusyBeeIcon className="w-[22px] h-[22px]" />
              <span className="text-[15px] tracking-tight text-foreground"><span className="font-semibold">Busy</span><span className="text-amber-600 dark:text-amber-400">Bee.Day</span></span>
            </Link>
          </div>

          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setNewFor(newContext())} title={`${t('nav.create.new')} (${newShortcutLabel})`}
              aria-label={t('nav.create.new')} data-testid="tablet-new"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
              <Plus className="w-[18px] h-[18px]" />
            </button>
            <button
              type="button"
              onClick={() => setGlobalSearchOpen(true)}
              title={`${t('nav.search')} (${shortcutLabel})`}
              aria-label={t('nav.search')}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Search className="w-4 h-4" />
            </button>
            <NotificationBell className="w-9 h-9 flex items-center justify-center rounded-lg" />
            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(prev => !prev)}
                className="flex items-center p-1.5 rounded-lg hover:bg-accent transition-colors duration-75"
                aria-label={user?.full_name}
              >
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt={user.full_name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-honey-ink font-medium text-sm flex-shrink-0">
                    {user?.full_name?.charAt(0) || 'U'}
                  </div>
                )}
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-xl shadow-2xl shadow-black/20 z-50 py-1 ring-1 ring-border">
                  {/* User info */}
                  <div className="px-4 py-3 border-b border-border/60">
                    <div className="flex items-center gap-3">
                      {user?.avatar_url ? (
                        <img src={user.avatar_url} alt={user.full_name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center text-black font-medium shrink-0">
                          {user?.full_name?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-foreground text-sm font-medium truncate">{user?.full_name}</p>
                        <p className="text-muted-foreground text-xs truncate">{user?.email}</p>
                        {user?.plan && (
                          <span className="inline-block mt-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-muted text-muted-foreground">
                            {user.plan}
                            {user.subscription_status === 'trialing' && ` · ${t('nav.trial')}`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Actions */}
                  <Link
                    to="/app/bots"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground/75 hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <Bot className="w-4 h-4" />
                    {t('nav.manage_bots')}
                  </Link>
                  <Link
                    to="/app/subscription"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground/75 hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    {t('nav.subscription')}
                  </Link>
                  <Link
                    to="/app/settings"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground/75 hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    {t('nav.settings')}
                  </Link>
                  <button
                    onClick={() => { setShowUserMenu(false); handleLogout(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground/80 hover:text-red-400 hover:bg-red-500/5 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>}

        {/* The content sits on an inset panel on desktop (Honey graphite) */}
        <div className="flex-1 min-h-0 flex flex-col lg:rounded-[14px] lg:border lg:border-border lg:bg-card overflow-hidden">
        {/* Timezone mismatch banner */}
        {timezoneMismatch && (
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap text-sm">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <Globe className="w-4 h-4 flex-shrink-0" />
              <span>
                {t('settings.datetime.timezone_change_detected', { detected: timezoneMismatch.detected, stored: timezoneMismatch.stored })}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={acceptTimezoneChange}
                className="px-3 py-1 bg-amber-500 text-black text-xs rounded-md font-medium hover:bg-amber-400 transition-colors"
              >
                {t('settings.datetime.update_to', { tz: timezoneMismatch.detected })}
              </button>
              <button
                onClick={dismissTimezoneMismatch}
                className="px-3 py-1 text-muted-foreground text-xs hover:text-foreground transition-colors"
              >
                {t('settings.datetime.keep', { tz: timezoneMismatch.stored })}
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <main ref={mainScrollRef} className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden relative ${fullBleed ? 'max-md:flex max-md:flex-col max-md:overflow-hidden' : ''} ${wideBleed ? 'md:flex md:flex-col md:overflow-hidden' : ''} ${docFit ? 'flex flex-col overflow-hidden' : ''}`}>
          <PullToRefreshIndicator pullDistance={pullDistance} refreshing={refreshing} />
          <EmailVerificationBanner />
          <div
            // One padding rule per width: md:p-0 next to md:p-6 loses to it (the CSS order),
            // which left Chats with 24 px of padding it is drawn without.
            className={`px-3 pt-2 pb-[calc(64px+env(safe-area-inset-bottom)+16px)] ${wideBleed ? 'md:p-0 md:flex-1 md:min-h-0' : 'md:p-6'} ${fullBleed ? 'max-md:p-0 max-md:flex-1 max-md:min-h-0' : ''} ${docFit ? 'flex-1 min-h-0 flex flex-col' : ''}`}
            style={{
              transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
              transition: refreshing ? 'transform 0.2s' : (pullDistance === 0 ? 'transform 0.2s' : undefined),
            }}
          >
            {/* No route-key wrapper — keying on pathname here would force every
                page (incl. FullCalendar-heavy Planner) to fully unmount and
                remount on every navigation, which costs hundreds of ms. The
                page-enter animation now lives inside each page's wrapper, so we
                get the visual transition without paying the unmount tax.
                Suspense lives here (not at App root) so the chrome — sidebar,
                bottom nav, mobile header — stays mounted while a lazy route
                chunk is loading and only the content area shows a skeleton. */}
            <Suspense fallback={<RouteChunkFallback />}>
              {phoneTabs ? <TabScreens scrollRef={mainScrollRef}><Outlet /></TabScreens> : <Outlet />}
            </Suspense>
          </div>
        </main>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[280px] bg-background border-r border-border flex flex-col">
            {renderSidebarHead(() => setMobileMenuOpen(false), () => setMobileMenuOpen(false))}
            <div className="flex-1 overflow-y-auto flex flex-col">
              {renderSidebarContent(() => setMobileMenuOpen(false))}
            </div>
            {renderPlanFooter(() => setMobileMenuOpen(false))}
          </aside>
        </div>
      )}
      
      {/* Global Task Creation + Detail */}
      <TaskCreationDialog />
      <TaskDetailPanel />

      {/* Global Search */}
      <SearchDialog
        open={globalSearchOpen}
        onOpenChange={setGlobalSearchOpen}
        onTaskSelect={handleSearchTaskSelect}
      />

      {/* Ask AI (Cmd/Ctrl + I) */}

      {/* Add Project Modal */}
      <AddProjectDialog
        isOpen={showAddProjectModal}
        onClose={closeAddProject}
        onProjectCreated={handleProjectCreated}
        defaultTeamId={addProjectForTeam}
      />

      {/* Project Context Menu */}
      <ProjectContextMenu
        show={contextMenu.show}
        x={contextMenu.x}
        y={contextMenu.y}
        project={contextMenu.project}
        onClose={closeContextMenu}
        onEdit={() => {
          navigate(`/app/projects?project=${contextMenu.project?.id}&action=edit`);
          closeContextMenu();
        }}
        onDelete={() => {
          window.dispatchEvent(new CustomEvent('deleteProjectRequest', { detail: { project: contextMenu.project } }));
          closeContextMenu();
        }}
        onDuplicate={async () => {
          try {
            // BG_91: server-side deep copy - duplicates the project together
            // with its tasks, views, sections and documents. The old client-side
            // call only re-created the shell, landing as an empty project. The
            // axios interceptor invalidates ['projects'] so the sidebar refetches.
            await projectsApi.duplicate(contextMenu.project?.id);
            fetchSidebarData();
          } catch (err) {
            // A 402 means the project quota is hit - the api interceptor already
            // opens the upgrade modal, so nothing extra to do here.
            console.error('Error duplicating project:', err);
          }
          closeContextMenu();
        }}
        onToggleFavorite={handleToggleFavorite}
        onAddToTeam={handleAddToTeam}
        // Only the favorites keep an order of the person's; "Projects" lists the latest opened.
        onMoveAbove={contextMenu.project?.is_favorite ? (p) => {
          moveProject(sidebarFavorites, p.id, -1, 'favorites');
          closeContextMenu();
        } : undefined}
        onMoveBelow={contextMenu.project?.is_favorite ? (p) => {
          moveProject(sidebarFavorites, p.id, 1, 'favorites');
          closeContextMenu();
        } : undefined}
        onShare={(p) => { setShareModalProject(p); closeContextMenu(); }}
        onActivityLog={(p) => { setActivityLogProject(p); closeContextMenu(); }}
      />

      {/* Delete Project Modal */}
      <DeleteProjectModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setProjectToDelete(null); }}
        project={projectToDelete}
        onConfirmDelete={async (project) => {
          try {
            await projectsApi.delete(project.id);
            // projectDeleted is a UI-routing signal (ProjectsPage navigates
            // away from the deleted project); the data side is invalidated
            // by the axios interceptor.
            window.dispatchEvent(new CustomEvent('projectDeleted', { detail: { projectId: project.id } }));
            pushUndo({
              label: t('projects.project_deleted', { name: project.name }),
              undo: async () => {
                await trashApi.restoreProject(project.id);
                // axios interceptor invalidates projects + trash + tasks queries.
              },
            });
          } catch (err) {
            console.error('Error deleting project:', err);
            throw err; // Re-throw so DeleteProjectModal can handle it
          }
        }}
      />

      {/* Sidebar "+" add menu (same pattern as ProjectContextMenu) */}
      {addMenu.show && (() => {
        const closeAddMenu = () => setAddMenu({ show: false, x: 0, y: 0, projectId: null, parentId: null });
        const createDoc = (type) => { handleCreateDocument(addMenu.projectId, type, addMenu.parentId); closeAddMenu(); };
        const menuProject = (projects || []).find((p) => p.id === addMenu.projectId);
        // A folder holds documents only; the project itself gets every kind of new thing.
        const inFolder = !!addMenu.parentId;
        const onChat = () => {
          closeAddMenu();
          setMobileMenuOpen(false);
          navigate(`/app/projects?project=${addMenu.projectId}&panel=chat`);
        };
        return (
          <>
            <div className="fixed inset-0 z-50" onClick={closeAddMenu} onKeyDown={(e) => e.key === 'Escape' && closeAddMenu()} />
            <div className="fixed z-50" style={{ top: addMenu.y, left: Math.max(8, addMenu.x) }}
              onKeyDown={(e) => e.key === 'Escape' && closeAddMenu()}>
              <CreateMenu
                project={menuProject}
                onTask={inFolder ? undefined : () => { closeAddMenu(); setMobileMenuOpen(false); openTaskCreation({ projectId: addMenu.projectId }); }}
                onList={inFolder ? undefined : () => { handleCreateSection(addMenu.projectId); closeAddMenu(); }}
                onDocument={createDoc}
                onChat={inFolder ? undefined : onChat}
              />
            </div>
          </>
        );
      })()}

      {/* Section/Document context menu */}
      {itemMenu.show && (
        <>
          <div className="fixed inset-0 z-50" onClick={closeItemMenu} />
          <div
            ref={itemMenuRef}
            className="fixed w-44 bg-card border border-border rounded-lg shadow-xl z-50 py-1"
            style={{ left: itemMenu.x, top: itemMenu.y }}
          >
            <button
              onClick={() => {
                setRenameValue(itemMenu.type === 'section' ? itemMenu.item.name : (itemMenu.item.title || ''));
                setRenamingItem({ type: itemMenu.type, id: itemMenu.item.id });
                closeItemMenu();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-muted-foreground hover:text-foreground hover:bg-accent text-left"
            >
              <Pencil className="w-3.5 h-3.5" /> {t('common.rename')}
            </button>
            <button
              onClick={() => {
                const url = itemMenu.type === 'section'
                  ? `${window.location.origin}/app/projects?project=${itemMenu.projectId}&section=${itemMenu.item.id}`
                  : `${window.location.origin}/app/documents/${itemMenu.item.id}`;
                navigator.clipboard.writeText(url);
                closeItemMenu();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-muted-foreground hover:text-foreground hover:bg-accent text-left"
            >
              <Link2 className="w-3.5 h-3.5" /> {t('documents.copy_link')}
            </button>
            <div className="my-1 border-t border-border" />
            <button
              onClick={() => {
                if (itemMenu.type === 'section') {
                  handleDeleteSection(itemMenu.item, itemMenu.projectId);
                } else {
                  handleDeleteDocument(itemMenu.item);
                }
                closeItemMenu();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-red-400 hover:bg-red-500/10 text-left"
            >
              <Trash2 className="w-3.5 h-3.5" /> {t('tasks.move_to_trash')}
            </button>
          </div>
        </>
      )}

      {/* Keyboard Shortcuts Modal */}
      {showShortcutsModal && (
        <KeyboardShortcutsModal onClose={() => setShowShortcutsModal(false)} />
      )}

      {/* Add Project to Team Modal */}
      <AddProjectToTeamDialog
        show={showAddToTeamModal}
        onClose={closeAddToTeam}
        project={projectToAddToTeam}
        onSuccess={handleAddToTeamSuccess}
      />

      {/* Share Project Modal */}
      <ShareProjectDialog
        isOpen={!!shareModalProject}
        onClose={closeShareProject}
        project={shareModalProject}
      />

      {/* Activity Log */}
      <ActivityLogDialog
        project={activityLogProject}
        onClose={closeActivityLog}
      />

      {/* Post-registration onboarding (4 short steps, all skippable). */}
      <OnboardingDialog open={!!showOnboarding} onClose={closeOnboarding} />

      {/* One-time dim+spotlight pointing at the Tutorials icon. Self-gated by
          tutorial_first_view_seen so it never re-appears after dismissal. */}
      <Spotlight />

      {/* New: the one input and the create tiles (Ctrl+K, "+ New", the phone's "+"). */}
      <NewDialog
        open={!!newFor}
        onOpenChange={closeNew}
        project={newProject}
        sectionId={newProject ? newFor?.sectionId : null}
        onTask={openTaskCreation}
        onList={handleCreateSection}
        onDocument={handleCreateDocument}
        onChat={startChat}
        onProject={addProjectFromNew}
      />

      {/* Mobile shell: the tab bar (with "+") and the More sheet. Visible below md. */}
      <MobileBottomNav />
      <MoreSheet open={mobileMoreOpen} onOpenChange={setMobileMoreOpen} />
    </div>
    </BotsProvider>
    </NotificationsProvider>
    </ShellActionsContext.Provider>
    </MobileTitleContext.Provider>
  );
};

export default AppLayout;
